/**
 * Abgleich der lokalen Materialdateien (localFiles.ts) mit dem Server – immer
 * im Hintergrund, nie als Voraussetzung zum Öffnen oder Speichern.
 *
 * Hoch: jede lokal geänderte oder neu angelegte Datei, die neueste Fassung
 * gewinnt. Hat sich die Datei inzwischen auch auf dem Server geändert (anderes
 * Gerät), bleibt dieser Stand vorher als Version auf dem Gerät erhalten –
 * verloren geht nichts, auch wenn zwei Geräte gleichzeitig gezeichnet haben.
 *
 * Runter: Dateien, die auf dem Server neuer sind oder hier noch fehlen, werden
 * einzeln nacheinander geladen. Einzeln, damit ein schwaches WLAN nicht von
 * zehn parallelen Downloads verstopft wird, während man gerade etwas öffnet.
 */

import { pb } from "@/lib/pocketbase";
import { materialsService } from "../materials.service";
import { offlineStore } from "../offline/offline-store";
import { localFiles, type LocalFile } from "./localFiles";
import type { Material } from "@/types/models";

/** Ton/Video gehören nicht ins Arbeits-PDF und sind groß – die bleiben auf dem Server. */
function isMediaFile(m: Material): boolean {
  const type = (m.type ?? "").toLowerCase();
  return type.startsWith("audio/") || type.startsWith("video/") || /\.(mp3|m4a|aac|wav|ogg|opus|mp4|mov|webm|avi)$/i.test(m.file);
}

/** Lädt eine Datei vom Server und legt sie lokal ab. */
export async function downloadToLocal(m: Material): Promise<LocalFile> {
  const res = await fetch(materialsService.urlFor(m));
  if (!res.ok) throw new Error(`${m.title}: ${res.status} ${res.statusText}`);
  return localFiles.putRemote(m, await res.blob());
}

let pushing: Promise<{ pushed: number; failed: number }> | null = null;
let pushTimer: number | undefined;

/** Ausstehende Uploads bald anstoßen (gebündelt – beim Zeichnen wird alle paar Sekunden gespeichert). */
export function schedulePush(delayMs = 4_000): void {
  window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => void pushPending().catch(() => undefined), delayMs);
}

/** Alles Ausstehende hochladen. Läuft nie doppelt; Fehler lassen die Datei einfach ausstehend. */
export function pushPending(): Promise<{ pushed: number; failed: number }> {
  if (pushing) return pushing;
  pushing = (async () => {
    let pushed = 0;
    let failed = 0;
    if (!navigator.onLine) return { pushed, failed: (await localFiles.pending()).length };
    for (const file of await localFiles.pending()) {
      try {
        await pushOne(file);
        pushed++;
      } catch {
        failed++;
      }
    }
    return { pushed, failed };
  })().finally(() => {
    pushing = null;
  });
  return pushing;
}

async function pushOne(file: LocalFile): Promise<void> {
  if (file.id.startsWith("local_")) {
    const created = await materialsService.uploadBlob(file.topic, file.blob, file.name, file.title, file.position);
    await localFiles.rekey(file.id, created, file.rev);
    // Die zwischengespeicherte Serverliste kennt die Datei noch nicht – ohne
    // Eintrag fiele sie bis zum nächsten Listenabruf aus der lokalen Ansicht.
    const cached = (await offlineStore.get<Material[]>(`base:materials:${file.topic}`)) ?? [];
    await offlineStore.set(`base:materials:${file.topic}`, [...cached.filter((m) => m.id !== created.id), created]);
    return;
  }

  // Frisch vom Server, am Lese-Cache vorbei: genau hier zählt der aktuelle Stand.
  const remote = await pb.collection("materials").getOne<Material>(file.id, { requestKey: null }).catch((err) => {
    if (err?.status === 404) return null;
    throw err;
  });
  if (!remote) {
    // Auf einem anderen Gerät gelöscht, hier noch bearbeitet: neu anlegen
    // statt die Änderung wegzuwerfen.
    const created = await materialsService.uploadBlob(file.topic, file.blob, file.name, file.title, file.position);
    await localFiles.rekey(file.id, created, file.rev);
    return;
  }
  if (file.remoteUpdated && remote.updated !== file.remoteUpdated) {
    // Gleichzeitig woanders geändert: deren Stand als Version sichern, dann
    // gewinnt die Fassung von diesem Gerät.
    await downloadToLocal(remote);
  }
  const saved = await materialsService.saveDetails(file.id, { title: file.title, file: file.blob, fileName: file.name });
  await localFiles.markUploaded(file.id, file.rev, saved);
}

/**
 * Serverliste eines Themas mit dem Gerät abgleichen: Neues und Geändertes
 * herunterladen, auf dem Server Gelöschtes hier entfernen. Liefert, ob sich
 * lokal etwas geändert hat (dann lohnt ein Neuaufbau des Arbeits-PDFs).
 */
export async function pullTopic(topic: string, server: Material[]): Promise<boolean> {
  const local = new Map((await localFiles.listTopic(topic)).map((f) => [f.id, f]));
  let changed = false;
  for (const m of server) {
    if (isMediaFile(m)) continue;
    const have = local.get(m.id);
    local.delete(m.id);
    if (have && (have.dirty || have.remoteUpdated === m.updated)) continue;
    await downloadToLocal(m);
    changed = true;
  }
  for (const gone of local.values()) {
    // Noch nie hochgeladen oder ungesicherte Änderung: bleibt, der Upload legt sie (wieder) an.
    if (gone.dirty || gone.id.startsWith("local_")) continue;
    await localFiles.remove(gone.id);
    changed = true;
  }
  return changed;
}

/** Alle Themen nacheinander abgleichen (App-Start, im Hintergrund). */
export async function pullAll(byTopic: Map<string, Material[]>): Promise<void> {
  for (const [topic, list] of byTopic) {
    if (!navigator.onLine) return;
    await pullTopic(topic, list).catch(() => undefined);
  }
}

let started = false;

/** Einmal beim App-Start: Uploads anstoßen, sobald wieder Netz da ist. */
export function startFileSync(): void {
  if (started) return;
  started = true;
  window.addEventListener("online", () => schedulePush(1_000));
  schedulePush(8_000);
}
